import { formatNumber } from '@mais-retorno-front/helpers/util-helpers';
import {
  capitalyzeText,
  formatCnpj,
} from '@mais-retorno-front/libs/util-helpers';
import { validateRedirect } from '@mais-retorno-front/services/redirect';
import { AxiosInstance } from 'axios';

export interface IManagerDetail {
  name: string;
  identifier: number;
  slug: string;
  company_or_person: string;
  participant_code: number;
  commercial_name: string | null;
  societary_control: string;
  asset_manager_address_info: IAssetManagerAddress | null;
  asset_manager_contact_info: IAssetManagerContact | null;
  networth: number | null;
  category: null;
  site: string;
  nicename: string;
  hide_info: boolean;
  board_of_directors: IBoardOfDirectors[];
  responsibles: IResponsibles[];
  stats: IManagerStats | null;
  partners: IPartners[];
  funds: IManagerAsset[];
}

interface IAssetManagerAddress {
  zip_code: number;
  state: string;
  street: string;
  address_complement: string;
  neighborhood: string;
  city: string;
}

interface IAssetManagerContact {
  telephone: string;
  fax: string;
  'e-mail': string;
}

interface IBoardOfDirectors {
  diretor: string;
  diretor_default: boolean;
}

interface IResponsibles {
  resp: string;
  desc_resp: string;
}

interface IManagerStats {
  status: IStatsStatus | null;
  networth: number;
  quota_holders: number;
  funds: number;
}

interface IStatsStatus {
  operacional?: number;
  cancelado?: number;
  'pre-operacional'?: number;
}

interface IPartners {
  socios: string;
}

interface IManagerAsset {
  slug: string;
  id: number;
  nicename: string;
  networth: number;
  status: IFundStatsType;
  fund_class: {
    class: IFundClass;
    subclass: IFundClass;
  };
  quota_holders: number;
}

type IFundStatsType = 'operacional' | 'pre-operacional' | 'cancelado';

interface IFundClass {
  name: string;
  slug: string;
}

export interface IManagerDetailsReturn {
  name: string;
  identifier: number;
  slug: string;
  nicename: string;
  director: string;
  hideInfo: boolean;
  site: string;
  email: string;
  phone: string;
  boardDetails: {
    label: string;
    value: any;
  }[];
  boardOfResponsibles: IResponsibles[];
  boardOfPartners: IPartners[];
  fundsUnderManagement: {
    mainInfo: {
      name: string;
      additionalText: string;
      link: string;
    };
    data: { slug: string; name: string; value: string }[];
  }[];
  statsDashboard: {
    patrimonio: {
      name: string;
      type: string;
      value: string;
    };
    cotistas: {
      name: string;
      type: string;
      value: string;
    };
    fundos: {
      name: string;
      type: string;
      value: number;
      operational: number;
      preOperational: number;
      canceled: number;
    };
  };
  notFound?: boolean;
  destination?: string;
}

interface IManagerDetailsErrorReturn {
  notFound?: boolean;
  destination?: string;
}

export class GetSheetManagerUseCase {
  constructor(private readonly http: AxiosInstance) {}

  async execute(
    managerSlug: string
  ): Promise<IManagerDetailsReturn | IManagerDetailsErrorReturn> {
    if (!managerSlug) return null;

    try {
      const { data } = await this.http.get<IManagerDetail>(
        `/asset-managers/details/${managerSlug}`
      );

      const operationalFunds = data.stats?.status?.operacional || 0;
      const preOperationalFunds = data.stats?.status?.['pre-operacional'] || 0;
      const canceledFunds = data.stats?.status?.cancelado || 0;
      const director =
        data.board_of_directors && !data.hide_info
          ? data.board_of_directors
              .map((director) => {
                if (director.diretor_default) {
                  return director.diretor;
                }
              })
              .join(', ')
          : null;

      return {
        name: data?.name || null,
        nicename: data?.nicename || null,
        identifier: data?.identifier || null,
        slug: data?.slug || null,
        hideInfo: data?.hide_info || null,
        director: director || null,
        site: data?.site || null,
        phone: data?.asset_manager_contact_info?.telephone || null,
        email: data?.asset_manager_contact_info?.['e-mail'] || null,
        boardDetails: [
          {
            label: 'Nome da gestora:',
            value: data?.name || null,
          },
          {
            label: 'Site:',
            value: data?.site || null,
          },
          {
            label: 'Diretor responsável:',
            value: director,
          },
        ],
        boardOfResponsibles: data?.responsibles || null,
        boardOfPartners: data?.partners || null,
        fundsUnderManagement:
          data?.funds.map((fund) => {
            return {
              mainInfo: {
                name: fund.nicename,
                additionalText: formatCnpj(fund.id)?.toString(),
                link: `fundo/${fund.slug}`,
              },
              data: [
                {
                  slug: fund.slug,
                  name: fund.fund_class.class.name,
                  value: fund.fund_class.subclass.name,
                },
                {
                  slug: 'status',
                  name: 'Status',
                  value: capitalyzeText(fund.status),
                },
                {
                  slug: 'pl',
                  name: 'PL',
                  value: formatNumber(fund.networth, {
                    type: 'money',
                    thousandMask: true,
                  }),
                },
                {
                  slug: 'cotistas',
                  name: 'Cotistas',
                  value: formatNumber(fund.quota_holders, {
                    thousandMask: true,
                    decimalPlaces: 2,
                  }),
                },
              ],
            };
          }) || null,
        statsDashboard: {
          patrimonio: {
            name: 'Patrimônio Líquido',
            type: 'patrimonio',
            value: formatNumber(data.stats?.networth, {
              thousandMask: true,
              type: 'money',
            }),
          },
          cotistas: {
            name: 'Número de cotistas',
            type: 'cotistas',
            value: formatNumber(data?.stats?.quota_holders),
          },
          fundos: {
            name: 'Total de fundos sob gestão',
            type: 'fundos',
            value: operationalFunds + preOperationalFunds + canceledFunds,
            operational: operationalFunds,
            preOperational: preOperationalFunds,
            canceled: canceledFunds,
          },
        },
      };
    } catch (error) {
      if (error.response?.status === 404) {
        const { new_path } = await validateRedirect(`/gestores/${managerSlug}`);
        if (new_path) {
          return {
            destination: new_path,
          };
        } else {
          return {
            notFound: true,
          };
        }
      }
      throw error;
    }
  }
}
